/**
 * Unified Payroll Rate Calculation Service
 * 
 * This service provides a single source of truth for calculating employee hourly rates
 * across all payroll engines, database functions, and UI components.
 * 
 * Addresses the critical performance issue where different calculation paths
 * (frontend vs database RPC) were producing inconsistent results (€120 vs €4,986).
 */

import { Employee, Role } from '@/types';
import { supabase } from '@/integrations/supabase/client';

export interface RateCalculationResult {
  hourlyRate: number;
  source: 'employee_direct' | 'role_with_adjustment' | 'role_default' | 'employment_type' | 'minimum_wage';
  details: {
    baseRate?: number;
    adjustment?: number;
    minimumWage?: number;
    countryCode?: string;
  };
}

export interface PayrollConstants {
  minimum_wage_hourly: number;
  year: number;
  country_code: string;
}

export class PayrollRateCalculationService {
  private static constantsCache = new Map<string, PayrollConstants>();
  private static cacheExpiryTime = 30 * 60 * 1000; // 30 minutes
  private static lastCacheUpdate = new Map<string, number>();

  /**
   * Calculate standardized hourly rate for an employee
   * This is the authoritative method used across all systems
   */
  public static async calculateStandardizedHourlyRate(
    employee: Employee,
    roleInfo?: Role | null,
    countryCode: string = 'SK'
  ): Promise<RateCalculationResult> {
    // 1. Check for direct employee hourly rate (highest priority)
    if (employee.hourly_rate && employee.hourly_rate > 0) {
      return {
        hourlyRate: employee.hourly_rate,
        source: 'employee_direct',
        details: {
          baseRate: employee.hourly_rate
        }
      };
    }

    // 2. Check for role-based rate with salary adjustment
    if (roleInfo?.default_pay_rate && roleInfo.default_pay_rate > 0) {
      let effectiveRate = this.convertToHourlyRate(roleInfo.default_pay_rate, roleInfo.pay_type);
      
      // Apply salary adjustment if present
      if (employee.salary_adjustment !== null && employee.salary_adjustment !== undefined) {
        effectiveRate += employee.salary_adjustment;
        
        return {
          hourlyRate: effectiveRate,
          source: 'role_with_adjustment',
          details: {
            baseRate: roleInfo.default_pay_rate,
            adjustment: employee.salary_adjustment
          }
        };
      }

      return {
        hourlyRate: effectiveRate,
        source: 'role_default',
        details: {
          baseRate: roleInfo.default_pay_rate
        }
      };
    }

    // 3. Check for salary adjustment without role rate
    if (employee.salary_adjustment !== null && employee.salary_adjustment !== undefined && employee.salary_adjustment > 0) {
      return {
        hourlyRate: employee.salary_adjustment,
        source: 'role_with_adjustment',
        details: {
          adjustment: employee.salary_adjustment
        }
      };
    }

    // 4. Fallback to minimum wage
    const minimumWage = await this.getMinimumWageHourly(countryCode);
    
    return {
      hourlyRate: minimumWage,
      source: 'minimum_wage',
      details: {
        minimumWage,
        countryCode
      }
    };
  }

  /**
   * Convert different pay types to hourly rates
   */
  private static convertToHourlyRate(rate: number, payType: string): number {
    switch (payType) {
      case 'hourly':
        return rate;
      case 'salary':
        // Convert yearly salary to hourly (40 hours/week, 52 weeks/year)
        return rate / (40 * 52);
      case 'fixed_contract':
        // For fixed contracts, we assume a standard work year
        return rate / (40 * 52);
      default:
        return rate; // Assume hourly if unknown
    }
  }

  /**
   * Get minimum wage for a country with caching
   */
  private static async getMinimumWageHourly(countryCode: string): Promise<number> {
    const cacheKey = `${countryCode}_${new Date().getFullYear()}`;
    const now = Date.now();
    
    // Check cache first
    if (this.constantsCache.has(cacheKey)) {
      const lastUpdate = this.lastCacheUpdate.get(cacheKey) || 0;
      if (now - lastUpdate < this.cacheExpiryTime) {
        return this.constantsCache.get(cacheKey)!.minimum_wage_hourly;
      }
    }

    // Fetch from database
    const constants = await this.fetchPayrollConstants(countryCode);
    
    // Update cache
    this.constantsCache.set(cacheKey, constants);
    this.lastCacheUpdate.set(cacheKey, now);
    
    return constants.minimum_wage_hourly;
  }

  /**
   * Fetch payroll constants from database
   */
  private static async fetchPayrollConstants(countryCode: string): Promise<PayrollConstants> {
    const currentYear = new Date().getFullYear();
    
    // Try current year first
    let { data, error } = await supabase
      .from('payroll_constants')
      .select('minimum_wage_hourly, year, country_code')
      .eq('country_code', countryCode)
      .eq('year', currentYear)
      .single();

    if (error || !data) {
      // Fallback to most recent year available
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('payroll_constants')
        .select('minimum_wage_hourly, year, country_code')
        .eq('country_code', countryCode)
        .order('year', { ascending: false })
        .limit(1)
        .single();

      if (fallbackError || !fallbackData) {
        // Hard fallback values by country
        const fallbackRates: Record<string, number> = {
          'SK': 4.30, // Slovak minimum wage
          'AT': 12.00, // Austrian minimum wage (approximate)
          'DE': 12.00, // German minimum wage
          'FR': 11.27, // French minimum wage (approximate)
        };

        return {
          minimum_wage_hourly: fallbackRates[countryCode] || 10.00,
          year: currentYear,
          country_code: countryCode
        };
      }

      data = fallbackData;
    }

    return {
      minimum_wage_hourly: data.minimum_wage_hourly,
      year: data.year,
      country_code: data.country_code
    };
  }

  /**
   * Batch calculate rates for multiple employees (performance optimization)
   */
  public static async batchCalculateRates(
    employees: (Employee & { role?: Role })[], 
    countryCode: string = 'SK'
  ): Promise<Map<string, RateCalculationResult>> {
    const results = new Map<string, RateCalculationResult>();
    
    // Pre-load minimum wage to avoid multiple database calls
    const minimumWage = await this.getMinimumWageHourly(countryCode);
    
    for (const employee of employees) {
      const result = await this.calculateStandardizedHourlyRate(
        employee, 
        employee.role, 
        countryCode
      );
      results.set(employee.id, result);
    }
    
    return results;
  }

  /**
   * Clear the constants cache (for testing or when constants are updated)
   */
  public static clearCache(): void {
    this.constantsCache.clear();
    this.lastCacheUpdate.clear();
  }

  /**
   * Validate rate calculation result
   */
  public static validateRateCalculation(result: RateCalculationResult): boolean {
    // Basic validation rules
    if (result.hourlyRate <= 0) {
      return false;
    }
    
    // Rate should not be unreasonably high (safety check)
    if (result.hourlyRate > 1000) {
      return false;
    }
    
    return true;
  }

  /**
   * Get human-readable explanation of rate calculation
   */
  public static explainRateCalculation(result: RateCalculationResult): string {
    switch (result.source) {
      case 'employee_direct':
        return `Direct employee hourly rate: €${result.hourlyRate.toFixed(2)}/hr`;
      
      case 'role_with_adjustment': {
        const base = result.details.baseRate || 0;
        const adjustment = result.details.adjustment || 0;
        return `Role rate (€${base.toFixed(2)}) + adjustment (€${adjustment.toFixed(2)}) = €${result.hourlyRate.toFixed(2)}/hr`;
      }
      
      case 'role_default':
        return `Role default rate: €${result.hourlyRate.toFixed(2)}/hr`;
      
      case 'employment_type':
        return `Employment type rate: €${result.hourlyRate.toFixed(2)}/hr`;
      
      case 'minimum_wage':
        return `Minimum wage fallback (${result.details.countryCode}): €${result.hourlyRate.toFixed(2)}/hr`;
      
      default:
        return `Unknown rate source: €${result.hourlyRate.toFixed(2)}/hr`;
    }
  }
}